import { ModelField } from "../database/ModelField";
import { FieldType } from "../type-system/FieldType";
import { ShopifyShopKey } from "./shopify/keys";
import { ShopifyDominion } from "./shopify/ShopifyDominion";
import { getRootWebhookModelKey } from "./shopify/ShopifyModelMetadata";
import { ConnectionModelFieldSpec } from "./utils";

/**
 * Uses the legacy way of determining if a field is a sync-only field when the model was still syncing via REST sync config
 * @param field - The ModelField to check
 * @returns True if the field is a legacy sync-only field, false otherwise
 */
const isLegacyShopifySyncOnlyField = (field: ModelField | ConnectionModelFieldSpec) => {
  return !!field.contributorConfiguration?.[ShopifyDominion.id]?.graphQLOnlyField;
};

const isBelongsToShopModelField = (field: ModelField | ConnectionModelFieldSpec) => {
  return field.configuration?.relatedModelKey === ShopifyShopKey;
};

export const isShopifyWebhookField = (field: ModelField | ConnectionModelFieldSpec) => {
  const shopifyDominionConfig = field.contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig || !shopifyDominionConfig.availableInWebhook) return false;

  return shopifyDominionConfig.availableInWebhook.length > 0;
};

/**
 * Checks if a field is a standardized sync-only field based on the expected sync only field configuration post GraphQL migration, where there is a graphQLFieldKey and no shopifyFieldKey in the field configuration
 * @param field - The ModelField to check
 * @returns True if the field is a standardized sync-only field, false otherwise
 */
const isStandardizedSyncOnlyField = (field: ModelField | ConnectionModelFieldSpec) => {
  const shopifyDominionConfig = field.contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig) return false;

  const hasGraphQLFieldKey = !!shopifyDominionConfig.graphQLFieldKey;
  const hasShopifyFieldKey = !!shopifyDominionConfig.shopifyFieldKey;

  return hasGraphQLFieldKey && !hasShopifyFieldKey;
};

/**
 * Checks if a field is a root Shopify webhook model field, meaning it is the root model of the webhook
 * @param field - The ModelField to check
 * @returns True if the field is a root Shopify webhook model field, false otherwise
 */
const isRootShopifyWebhookModelField = (field: ModelField) => {
  return getRootWebhookModelKey(field.model.key) === field.model.key;
};
export const isBelongsToParentShopifyWebhookModelField = (field: ModelField) => {
  if (field.fieldType !== FieldType.BelongsTo) return false;

  const parentModelKey = getRootWebhookModelKey(field.model.key);

  return field.configuration.relatedModelKey === parentModelKey;
};

export const isShopifySyncOnlyField = (field: ModelField | ConnectionModelFieldSpec) => {
  const shopifyDominionConfig = field.contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig) return false;

  // We always set the Shop field
  if (isBelongsToShopModelField(field)) return false;

  if (isLegacyShopifySyncOnlyField(field)) {
    return true;
  }

  if (isShopifyWebhookField(field)) {
    return false;
  }

  return isStandardizedSyncOnlyField(field);
};

/**
 * Checks if a field is one that is implicitly synced on webhook by the Gadget underlying sync system (and not via data from a webhook payload)
 * @param field - The ModelField to check
 * @returns True if the field is a field that is synced on webhook, false otherwise
 */
export const isFieldGadgetSystemSyncedOnWebhook = (field: ModelField) => {
  // Cases where the field is implicitly synced on webhook
  // - Shop field
  // - System dominion fields
  // - BelongsTo parent fields to its parent model
  if (isBelongsToShopModelField(field) || field.isGadgetSystemDominion || isBelongsToParentShopifyWebhookModelField(field)) return true;

  return false;
};
